import './ProductTile.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';

import { Button, ButtonVariant } from './Button';
import { CustomRadio } from './CustomRadio';
import { t } from './i18n/i18n';
import { Icon, IconName, IconSize } from './Icon';
import { PriceBlock } from './PriceBlock';

export interface ProductTileProps extends b2x.ProductTileProps {
  light?: boolean;
}

export const ProductTile = ({ light, product, ...otherProps }: ProductTileProps) => {
  const inStock = product.skus?.find((sku) => sku.state === 'AVAILABLE');

  return (
    <b2x.ProductTile
      className={classnames(
        'card',
        { 'in-stock': inStock },
        { 'out-of-stock': !inStock },
        { 'product-tile-light': light }
      )}
      product={product}
      {...otherProps}
      enableExtraInfo
    />
  );
};

export type ProductTileBasicInfoProps = b2x.ProductTileBasicInfoProps;

export const ProductTileBasicInfo = (props: ProductTileBasicInfoProps) => (
  <b2x.ProductTileBasicInfo {...props} customComponent={CustomProductTileBasicInfo} />
);

const CustomProductTileBasicInfo = ({
  colIndex,
  imageFormat,
  product,
  productsPerRow,
  sku,
}: b2x.ProductTileBasicInfoProps) => {
  const productImage = sku?.image ?? product.image;
  const productAlternativeImage =
    (sku?.alternativeImages && sku.alternativeImages[0]) ?? (product.alternativeImages && product.alternativeImages[0]);

  return (
    <div className="product-tile-basic-info">
      <div className="position-relative">
        <b2x.router.Link to={product.url}>
          <div className="product-media-container">
            <b2x.EqualHeightElement
              colIndex={colIndex}
              name={productsPerRow ? `product-media-container-${productsPerRow}` : 'product-media-container'}
            >
              <div className="image">
                <b2x.ImageSwitcher
                  alt={product.name}
                  aspectRatio={b2x.appConfig.productImageAspectRatio}
                  fluid
                  format={imageFormat}
                  src={productImage?.src}
                />
              </div>
              {productImage && productAlternativeImage && (
                <div className="alternative-image">
                  <b2x.ImageSwitcher
                    alt={product.name}
                    aspectRatio={b2x.appConfig.productImageAspectRatio}
                    fluid
                    format={imageFormat}
                    src={productAlternativeImage.src}
                  />
                </div>
              )}
            </b2x.EqualHeightElement>
          </div>
        </b2x.router.Link>
      </div>
    </div>
  );
};

export type ProductTileExtraInfoProps = b2x.ProductTileExtraInfoProps;

export const ProductTileExtraInfo = (props: ProductTileExtraInfoProps) => (
  <b2x.ProductTileExtraInfo customComponent={CustomProductTileExtraInfo} {...props} />
);

const CustomProductTileExtraInfo = ({
  colIndex,
  fieldsHelper,
  priceHelper,
  product,
  productsPerRow,
  selectedSku,
}: ProductTileExtraInfoProps) => {
  const inStock = product.skus?.find((sku) => sku.state === 'AVAILABLE');

  return (
    <div className="product-tile-extra-info pt-3" style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <b2x.router.Link className="text-decoration-none" to={product.url}>
        <div className="product-info-container">
          <b2x.EqualHeightElement colIndex={colIndex} name={`product-name-container-${productsPerRow}`}>
            <h3 className="fw-bold h6 mb-0">{product.name}</h3>
            {product.descriptionShort && <div className="text-uppercase fw-light">{product.descriptionShort}</div>}
          </b2x.EqualHeightElement>
          <b2x.EqualHeightElement colIndex={colIndex} name={`product-secondary-info-container-${productsPerRow}`}>
            {selectedSku?.name && <div className="text-uppercase fw-light small">{selectedSku.name}</div>}
            {/* <b2x.SummaryReviewsPreview productId={product.id} /> */}
          </b2x.EqualHeightElement>
        </div>
      </b2x.router.Link>

      <b2x.EqualHeightElement colIndex={colIndex} name={`product-atc-container-${productsPerRow}`}>
        {inStock ? (
          <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%' }}>
            <div className="my-2">
              {fieldsHelper.skusFieldSet.skus.formFields.length > 1 && (
                <div className="product-skus">
                  <fieldset>
                    <legend className="visually-hidden">{fieldsHelper.skusFieldSet.legend}</legend>
                    <b2x.FormGroup
                      {...fieldsHelper.skusFieldSet.skus.formGroup}
                      label={undefined}
                      labelClassName="extra-small"
                      noMarginBottom
                    >
                      <b2x.SwiperContext>
                        {({ navigationNextElRef, navigationPrevElRef }) => {
                          return (
                            <b2x.Div className="slider-container">
                              <Button
                                aria-label={t('accessibility.button.swiper_prev')}
                                className="prev"
                                iconStart={{ name: 'light-arrow-left' }}
                                innerRef={navigationPrevElRef}
                                justifyContent="start"
                                variant="blank"
                              />
                              <b2x.Swiper
                                navigation={{ custom: true }}
                                slides={fieldsHelper.skusFieldSet.skus.formFields.map(({ radio, sku }) => {
                                  const imageUrl = sku.attributes?.find(
                                    (attribute) => attribute.typeCode === 'BSK_ICONA_COLORE'
                                  )?.value;

                                  const label = imageUrl
                                    ? sku.attributes
                                        ?.find((attribute) => attribute.typeCode === 'BSK_NOME_VARIANTE')
                                        ?.value?.toLocaleLowerCase() ?? ''
                                    : sku.attributes
                                        ?.find((attribute) => attribute.typeCode === 'BSK_NOME_VARIANTE')
                                        ?.value?.toLocaleLowerCase() ??
                                      sku.name?.toLocaleLowerCase() ??
                                      '';

                                  const type = imageUrl ? 'icon' : 'text';
                                  return (
                                    <CustomRadio
                                      image={imageUrl ? { alt: label, title: label, url: imageUrl } : undefined}
                                      key={radio.id}
                                      label={label}
                                      radio={radio}
                                      type={type}
                                    />
                                  );
                                })}
                                slidesPerView={'auto'}
                                spaceBetween={8}
                              />
                              <Button
                                aria-label={t('accessibility.button.swiper_next')}
                                className="next"
                                iconStart={{ name: 'light-arrow-right' }}
                                innerRef={navigationNextElRef}
                                justifyContent="end"
                                variant="blank"
                              />
                            </b2x.Div>
                          );
                        }}
                      </b2x.SwiperContext>
                    </b2x.FormGroup>
                  </fieldset>
                </div>
              )}
            </div>
            {priceHelper && (
              <b2x.Div className="product-price-container" display="flex" flexDirection="column" gap={3}>
                <b2x.Div>
                  <PriceBlock hiddenDiscountPercentage priceHelper={priceHelper} />
                  {b2x.appConfig.enableBestPrice && <b2x.BestPrice priceHelper={priceHelper} />}
                </b2x.Div>
                <b2x.Div marginTop="auto" paddingBottom={1}>
                  {product.assembled && !product.kit ? (
                    <b2x.Div display={'grid'}>
                      <b2x.router.Link
                        className="btn btn-outline-primary d-flex gap-2 justify-content-center align-items-center"
                        to={product.url}
                      >
                        <Icon name={'plus'} size={12} />
                        {t('misc.assembledProduct.configure')}
                      </b2x.router.Link>
                    </b2x.Div>
                  ) : (
                    <b2x.AddToCartFormButton<ButtonVariant, IconName, IconSize>
                      fieldsHelper={fieldsHelper}
                      product={product}
                      selectedSku={selectedSku}
                      submitButton={{
                        iconStart: { name: 'plus', size: 12 },
                        label: t('misc.addToCart'),
                        variant: 'outline-primary',
                      }}
                    />
                  )}
                </b2x.Div>
              </b2x.Div>
            )}
          </div>
        ) : (
          <b2x.Div display="flex" flexDirection="column" justifyContent="end" paddingTop={3} style={{ height: '100%' }}>
            <Button disabled variant="outline-primary">
              {t('misc.productNotAvailable')}
            </Button>
          </b2x.Div>
        )}
      </b2x.EqualHeightElement>
      {/* <Button
        className="extra-small fw-normal"
        iconStart={{ name: 'try-rounded', size: 12 }}
        label={'Provalo subito'}
        onClick={handleTryButtonClick}
        variant="blank"
      /> */}
    </div>
  );
};
